import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, PackageOpen, ArrowRight } from "lucide-react";
import { api, CATEGORY_LABELS, CATEGORY_BADGE, stockBadge, formatPrice } from "@/api";
import MachineDetail from "@/components/MachineDetail";

const CATEGORIES = [
  { id: "all", label: "Tutte le Macchine" },
  { id: "domestiche", label: "Domestiche" },
  { id: "industriali", label: "Industriali" },
  { id: "ricamo", label: "Ricamo e Tagliacuci" },
];

export default function Catalog() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/machines").then((r) => setMachines(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = machines;
    if (category !== "all") list = list.filter((m) => m.category === category);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((m) => [m.name, m.brand, m.model].join(" ").toLowerCase().includes(needle));
    }
    const sorted = [...list];
    if (sort === "price_asc") sorted.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") sorted.sort((a, b) => b.price - a.price);
    else if (sort === "name_asc") sorted.sort((a, b) => a.name.localeCompare(b.name, "it"));
    return sorted;
  }, [machines, category, q, sort]);

  return (
    <section id="catalogo" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="catalog-section">
      <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-700">Il nostro catalogo</p>
      <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-900 mt-2">
        Macchine disponibili in negozio
      </h2>
      <p className="text-base sm:text-lg text-slate-600 mt-3 max-w-2xl">
        Ogni macchina è revisionata e testata dal nostro laboratorio. Scegli il modello e invia la richiesta: ti ricontattiamo noi.
      </p>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mt-8">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button key={c.id} data-testid={`filter-${c.id}`} onClick={() => setCategory(c.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                category === c.id ? "bg-sky-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-sky-300 hover:text-sky-700"
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex flex-1 gap-3 lg:justify-end">
          <div className="relative flex-1 lg:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome, marca o modello..." className="pl-9 bg-white" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="catalog-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="featured" data-testid="sort-featured">In Evidenza</SelectItem>
              <SelectItem value="price_asc" data-testid="sort-price-asc">Prezzo: dal più basso</SelectItem>
              <SelectItem value="price_desc" data-testid="sort-price-desc">Prezzo: dal più alto</SelectItem>
              <SelectItem value="name_asc" data-testid="sort-name-asc">Nome (A-Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10" data-testid="catalog-loading">
          {[1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" data-testid="catalog-empty">
          <PackageOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessuna macchina trovata con questi criteri.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10" data-testid="catalog-grid">
          {filtered.map((m) => {
            const sb = stockBadge(m.stock);
            return (
              <article key={m.id} data-testid={`machine-card-${m.id}`}
                className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_4px_20px_rgba(2,132,199,0.04)] hover:shadow-[0_12px_30px_rgba(2,132,199,0.1)] transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col">
                <div className="aspect-[4/3] bg-sky-50/50 overflow-hidden">
                  <img src={m.photos?.[0] || "https://images.unsplash.com/photo-1606501126768-b78d4569d3f9?q=85"} alt={m.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${CATEGORY_BADGE[m.category] || ""}`}>
                      {CATEGORY_LABELS[m.category] || m.category}
                    </span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${sb.cls}`}>{sb.label}</span>
                  </div>
                  <h3 className="font-sans font-semibold text-lg text-slate-900 mt-3 leading-snug">{m.name}</h3>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mt-1">{m.brand} {m.model && `· ${m.model}`}</p>
                  <div className="flex items-center justify-between mt-auto pt-4">
                    <span className="text-xl font-semibold text-sky-700">{formatPrice(m.price)}</span>
                    <Button size="sm" data-testid={`machine-detail-button-${m.id}`} onClick={() => setSelected(m)}
                      className="bg-sky-600 hover:bg-sky-700 text-white rounded-full gap-1.5">
                      Dettagli <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <MachineDetail machine={selected} open={!!selected} onClose={() => setSelected(null)} />
    </section>
  );
}
